import { ModelDataType } from 'src/app/interfaces/model-data-type';

import {
  Contact,
  MailingAddress,
  PrimaryContact,
  SiteFile,
  SiteUser,
  StreetAddress
} from '.';
import { Address } from './address';
import { BaseModel } from './base-model';
import { Contract } from './contract';
import { SiteVendor } from './site-vendor';

export class Site extends BaseModel {
  static SiteProperties: (
    | {
        property: keyof Site;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof Site;
        type: 'alias';
      }
    | {
        property: keyof Site;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'siteKey', type: 'column', dataType: 'string' },
    { property: 'paymentLinkId', type: 'column', dataType: 'string' },
    { property: 'legalName', type: 'column', dataType: 'string' },
    { property: 'units', type: 'column', dataType: 'number' },
    { property: 'dba', type: 'column', dataType: 'string' },
    { property: 'structure', type: 'column', dataType: 'string' },
    { property: 'ssn', type: 'column', dataType: 'string' },
    { property: 'ein', type: 'column', dataType: 'string' },
    { property: 'bsn', type: 'column', dataType: 'string' },
    { property: 'address1', type: 'column', dataType: 'string' },
    { property: 'address2', type: 'column', dataType: 'string' },
    { property: 'city', type: 'column', dataType: 'string' },
    { property: 'state', type: 'column', dataType: 'string' },
    { property: 'zip', type: 'column', dataType: 'string' },
    { property: 'latitude', type: 'column', dataType: 'string' },
    { property: 'longitude', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'phoneExt', type: 'column', dataType: 'string' },
    { property: 'fax', type: 'column', dataType: 'string' },
    { property: 'website', type: 'column', dataType: 'string' },
    { property: 'mailingAddress1', type: 'column', dataType: 'string' },
    { property: 'mailingAddress2', type: 'column', dataType: 'string' },
    { property: 'mailingCity', type: 'column', dataType: 'string' },
    { property: 'mailingState', type: 'column', dataType: 'string' },
    { property: 'mailingZip', type: 'column', dataType: 'string' },
    { property: 'logo', type: 'column', dataType: 'string' },
    { property: 'image', type: 'column', dataType: 'string' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'apiSource', type: 'column', dataType: 'string' },
    { property: 'apiKey', type: 'column', dataType: 'string' },
    { property: 'siteType', type: 'column', dataType: 'string' },

    { property: 'primaryContactName', type: 'alias' },
    { property: 'vendorsAssigned', type: 'alias' },
    { property: 'isUserAssigned', type: 'alias' },

    { property: 'Addresses', model: 'Address', type: 'association' },
    { property: 'StreetAddress', model: 'StreetAddress', type: 'association' },
    {
      property: 'MailingAddress',
      model: 'MailingAddress',
      type: 'association'
    },
    { property: 'Contracts', model: 'Contract', type: 'association' },
    { property: 'Contacts', model: 'Contact', type: 'association' },
    {
      property: 'PrimaryContact',
      model: 'PrimaryContact',
      type: 'association'
    },
    { property: 'SiteVendors', model: 'SiteVendor', type: 'association' },
    { property: 'SiteUsers', model: 'SiteUser', type: 'association' },
    { property: 'SiteFile', model: 'SiteFile', type: 'association' }
  ];

  static {
    Site.SiteProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        Site.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        Site.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        Site.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(SiteObject: Partial<Site>) {
    super();

    Site.SiteProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = SiteObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  siteKey?: string;
  paymentLinkId?: string | null;
  legalName?: string | null;
  units?: number | null;
  dba?: string | null;
  structure?: string | null;
  ssn?: string | null;
  ein?: string | null;
  bsn?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  phone?: string | null;
  phoneExt?: string | null;
  fax?: string | null;
  website?: string | null;
  mailingAddress1?: string | null;
  mailingAddress2?: string | null;
  mailingCity?: string | null;
  mailingState?: string | null;
  mailingZip?: string | null;
  logo?: string | null;
  image?: string | null;
  status?: 'Active' | 'Deactivated' | null;
  createdAt?: Date;
  updatedAt?: Date;
  CompanyId?: number;
  apiSource?: string | null;
  apiKey?: string | null;
  siteType?: string | null;

  primaryContactName?: string;
  vendorsAssigned?: string;
  isUserAssigned?: boolean;

  Contracts?: Contract[];
  Contacts?: Contact[];
  PrimaryContact?: PrimaryContact;
  SiteVendors?: SiteVendor[];
  SiteUsers?: SiteUser[];
  Addresses?: Address[];
  StreetAddress?: StreetAddress;
  MailingAddress?: MailingAddress;
  SiteFile?: SiteFile;
}
